import React, { useState } from 'react';
import MaterialTable from 'material-table';
import { Query, QueryResult } from 'material-table/types';
import { Button, Typography } from '@material-ui/core';
import { makeStyles, StylesProvider } from '@material-ui/core/styles';
import moment from 'moment';

import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import SpinnerOverlay from '../../../shared/components/SpinnerOverlay/SpinnerOverlay';
import SnackConfig from '../../../shared/config/snackConfig';
import { getAlarmGroups } from '../../../shared/services/alarm';
import { mainStyles } from '../../../shared/styles/common';
import { createTableIcons } from '../../../shared/styles/createTableIcons';
import { generateClassName } from '../../../shared/styles/generateClassName';

const useStyles = makeStyles({
  maxBorderBox: {
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  permalink: {
    textTransform: 'none',
    fontSize: 'inherit',
  },
});

const dateTimeFormat = 'MMM DD YYYY, hh:mm:ss a';

interface Group {
  id: number;
  startTime: string;
  endTime: string;
  numAlarms: number;
}

const Alarm: React.FC = () => {
  const classes = { ...mainStyles(), ...useStyles() };

  const [currentPage, setCurrentPage] = useState(0);
  const [firstCursor, setFirstCursor] = useState<number>();
  const [lastCursor, setLastCursor] = useState<number>();

  const [alarmGroupsLoading, setAlarmGroupsLoading] = useState(true);
  const [snackConfig, setSnackConfig] = useState(new SnackConfig({}));

  const setPage = (page: number, first?: number, last?: number) => {
    setCurrentPage(page);
    setFirstCursor(first);
    setLastCursor(last);
  };

  const resetPage = () => {
    setPage(0);
  };

  const fetchAlarmGroups = async (query: Query<Group>): Promise<QueryResult<Group>> => {
    const { page, pageSize } = query;
    const result: QueryResult<Group> = { data: [], page: 0, totalCount: 0 };

    setAlarmGroupsLoading(true);
    try {
      let cursor: number | undefined;
      let dir = 'next';
      if (page === 0) {
        dir = 'next';
      } else if (page === currentPage + 1) {
        cursor = lastCursor;
        dir = 'next';
      } else if (page === currentPage - 1) {
        cursor = firstCursor;
        dir = 'prev';
      } else {
        dir = 'prev';
      }
      const { groups, total } = await getAlarmGroups(pageSize, dir, cursor);
      if (groups.length === 0) {
        resetPage();
      } else {
        setPage(page, groups[0].id, groups[groups.length - 1].id);
        result.data = groups.map(({ id, startTime, endTime, alarmDetails }) => ({
          id,
          startTime,
          endTime,
          numAlarms: alarmDetails.length,
        }));
        result.page = page;
        result.totalCount = total;
      }
    } catch (error) {
      setSnackConfig(
        new SnackConfig({
          open: true,
          message: JSON.stringify(error),
        })
      );
    }
    setAlarmGroupsLoading(false);
    return result;
  };

  return (
    <div className={classes.container}>
      <div className={classes.maxBorderBox}>
        <SpinnerOverlay active={alarmGroupsLoading}>
          <StylesProvider generateClassName={generateClassName()}>
            <MaterialTable
              title="Alarms"
              options={{
                emptyRowsWhenPaging: false,
                search: false,
              }}
              columns={[
                {
                  title: '',
                  type: 'string',
                  // eslint-disable-next-line react/display-name
                  render: ({ id, startTime, endTime, numAlarms }: Group): React.ReactNode => (
                    <Button href={`/alarmDetails/${id}`} className={classes.permalink}>
                      <div>
                        Alarms from <b>{moment(startTime).format(dateTimeFormat)}</b> till{' '}
                        <b>{moment(endTime).format(dateTimeFormat)}</b>
                        <div>
                          <Typography color="textSecondary" variant="caption">
                            Total alarms - {numAlarms}
                          </Typography>
                        </div>
                      </div>
                    </Button>
                  ),
                },
              ]}
              data={fetchAlarmGroups}
              icons={createTableIcons()}
            />
          </StylesProvider>
        </SpinnerOverlay>
      </div>
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

export default Alarm;
